import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from 'bff/ApiClientProvider';
import {
  JustPortfolioDeleteRequest,
  JustPortfolioGetRequest,
  JustPortfolioPostRequest,
  Result,
  ResultStates
} from 'bff/client';
import { useInvalidateProposalInformation } from 'bff/hooks/common/ProposalsInfosApi';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { secondsToMilliseconds } from 'date-fns';

export const justPortfolioKeys = {
  base: () => ['just-portfolio'] as const,
  fetch: (request: JustPortfolioGetRequest) => [...justPortfolioKeys.base(), request] as const,
  submit: () => [...justPortfolioKeys.base(), 'submit'] as const
};

export const useInvalidateJustPortfolioDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: justPortfolioKeys.base(),
      refetchType: 'all'
    });
};

export const getJustProtfolioOptions = (getJustPortfolioGetRequest: JustPortfolioGetRequest) => {
  const { justPortfolioApi } = useApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: justPortfolioKeys.fetch(getJustPortfolioGetRequest),
    queryFn: () => justPortfolioApi.justPortfolioGet(getJustPortfolioGetRequest),
    select: (response) => response.data!
  });
};

export const useUpdateJustPortfolio = (
  options?: UseDisplayErrorMutationOptions<Result, JustPortfolioPostRequest>
) => {
  const { justPortfolioApi } = useApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateJustPortfolioDetails = useInvalidateJustPortfolioDetails();

  return useMutation({
    mutationKey: justPortfolioKeys.submit(),
    mutationFn: (request: JustPortfolioPostRequest) => justPortfolioApi.justPortfolioPost(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateJustPortfolioDetails()
        ]);
      }
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};

export const useDeleteJustPortfolio = (
  options?: UseDisplayErrorMutationOptions<Result, JustPortfolioDeleteRequest>
) => {
  const { justPortfolioApi } = useApiClient();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateJustPortfolioDetails = useInvalidateJustPortfolioDetails();

  return useMutation({
    mutationKey: justPortfolioKeys.submit(),
    mutationFn: (request: JustPortfolioDeleteRequest) =>
      justPortfolioApi.justPortfolioDelete(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          invalidateProposalInformation(),
          invalidateJustPortfolioDetails()
        ]);
      }
      options?.onSuccess?.(data, variables, ctx);
    }
  });
};
